import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './KeyVaultManager.css';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { getApiKey } from '../../helpers/stringHelpers';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

function KeyVaultManager({ config }) {
    const { accounts, instance } = useMsal();

    const [inventory, setInventory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRows, setExpandedRows] = useState({});
    const [sortField, setSortField] = useState('status');
    const [sortAsc, setSortAsc] = useState(true);

    // Check AD group membership (same pattern as TokenManager)
    useEffect(() => {
        const checkAccess = async () => {
            const requiredGroupIds = config.tokenCredentialGroupIds;
            if (!requiredGroupIds || !accounts || accounts.length === 0) return;

            try {
                const activeAccount = instance.getActiveAccount() || accounts[0];
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: ['https://graph.microsoft.com/.default'],
                    account: activeAccount,
                });
                const required = requiredGroupIds.split('|').filter(g => g.length > 0);
                const response = await fetch('https://graph.microsoft.com/v1.0/me/checkMemberGroups', {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${tokenResponse.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ groupIds: required }),
                });
                const data = await response.json();
                const access = data.value && data.value.length > 0;
                setHasAccess(access);
                if (access) fetchInventory();
            } catch {
                setHasAccess(false);
            }
        };
        checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchInventory = () => {
        setIsLoading(true);

        const accessTokenRequest = {
            scopes: [`api://${config.clientId}/User.Read`],
            account: instance.getActiveAccount() || accounts[0],
        };

        instance.acquireTokenSilent(accessTokenRequest).then((tokenResponse) => {
            const logmsgApiKey = getApiKey(config.enviromentScope);

            axios.get(config.logMsgApiUrl + '/GetKeyVaultInventory', {
                headers: {
                    'Authorization': 'Bearer ' + tokenResponse.accessToken,
                    'APIKEY': logmsgApiKey,
                },
            })
            .then(response => {
                setInventory(response.data || []);
                setIsLoading(false);
            })
            .catch(() => {
                setInventory([]);
                setIsLoading(false);
            });
        }).catch(() => {
            setIsLoading(false);
        });
    };

    const toggleRow = (index) => {
        setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const getWorstStatus = (credentials) => {
        if (!credentials || credentials.length === 0) return 'Unknown';
        if (credentials.some(c => c.status === 'Expired')) return 'Expired';
        if (credentials.some(c => c.status === 'ExpiringSoon')) return 'ExpiringSoon';
        if (credentials.some(c => c.status === 'Valid')) return 'Valid';
        return 'Unknown';
    };

    // True when at least one Key Vault reference does not match an active App Registration hint.
    const hasStaleRef = (entry) => Array.isArray(entry.keyVaultRefs)
        && entry.keyVaultRefs.some(r => r && r.isCurrent === false);

    // Outdated: stale ref where NO other ref for the same vault is current (critical — no valid replacement).
    const hasOutdatedRef = (entry) => {
        if (!Array.isArray(entry.keyVaultRefs)) return false;
        const staleVaults = entry.keyVaultRefs.filter(r => r && r.isCurrent === false).map(r => r.keyVaultName.toLowerCase());
        if (staleVaults.length === 0) return false;
        const currentVaults = new Set(entry.keyVaultRefs.filter(r => r && r.isCurrent === true).map(r => r.keyVaultName.toLowerCase()));
        return staleVaults.some(v => !currentVaults.has(v));
    };

    // Stale-only: stale ref where the same vault ALSO has a current ref (less critical — valid replacement exists).
    const hasStaleOnlyRef = (entry) => {
        if (!Array.isArray(entry.keyVaultRefs)) return false;
        const staleVaults = entry.keyVaultRefs.filter(r => r && r.isCurrent === false).map(r => r.keyVaultName.toLowerCase());
        if (staleVaults.length === 0) return false;
        const currentVaults = new Set(entry.keyVaultRefs.filter(r => r && r.isCurrent === true).map(r => r.keyVaultName.toLowerCase()));
        return staleVaults.some(v => currentVaults.has(v));
    };

    // True when two or more Key Vault references point to the same vault (duplicate vault entries).
    const hasDuplicateRef = (entry) => {
        if (!Array.isArray(entry.keyVaultRefs) || entry.keyVaultRefs.length < 2) return false;
        const vaultNames = entry.keyVaultRefs
            .filter(r => r && r.keyVaultName)
            .map(r => r.keyVaultName.toLowerCase());
        return vaultNames.length !== new Set(vaultNames).size;
    };

    // Get vault names that appear more than once in the refs list.
    const getDuplicateVaultNames = (entry) => {
        if (!Array.isArray(entry.keyVaultRefs) || entry.keyVaultRefs.length < 2) return new Set();
        const counts = {};
        entry.keyVaultRefs.forEach(r => {
            if (r && r.keyVaultName) {
                const name = r.keyVaultName.toLowerCase();
                counts[name] = (counts[name] || 0) + 1;
            }
        });
        return new Set(Object.keys(counts).filter(k => counts[k] > 1));
    };

    const statusOrder = { 'Expired': 0, 'ExpiringSoon': 1, 'Valid': 2, 'Unknown': 3 };

    const getStatusBadge = (status) => {
        const classMap = {
            'Expired': 'status-badge status-expired',
            'ExpiringSoon': 'status-badge status-expiring',
            'Valid': 'status-badge status-valid',
            'Unknown': 'status-badge status-unknown',
        };
        const labelMap = {
            'Expired': 'Expired',
            'ExpiringSoon': 'Expiring Soon',
            'Valid': 'Valid',
            'Unknown': 'Unknown',
        };
        return <span className={classMap[status] || 'status-badge'}>{labelMap[status] || status}</span>;
    };

    const truncateId = (id) => {
        if (!id || id.length <= 13) return id;
        return id.substring(0, 8) + '...' + id.substring(id.length - 4);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Filter inventory
    const filtered = inventory.filter(entry => {
        const term = searchTerm.toLowerCase();
        if (!term) return true;
        return (
            (entry.name || '').toLowerCase().includes(term) ||
            (entry.keyVaultName || '').toLowerCase().includes(term) ||
            (entry.clientId || '').toLowerCase().includes(term) ||
            (entry.appDisplayName || '').toLowerCase().includes(term) ||
            (entry.scope || '').toLowerCase().includes(term)
        );
    });

    // Sort inventory
    const sorted = [...filtered].sort((a, b) => {
        let cmp = 0;
        if (sortField === 'status') {
            cmp = (statusOrder[getWorstStatus(a.credentials)] || 3) - (statusOrder[getWorstStatus(b.credentials)] || 3);
        } else if (sortField === 'name') {
            cmp = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        } else if (sortField === 'keyVaultName') {
            cmp = (a.keyVaultName || '').localeCompare(b.keyVaultName || '', undefined, { sensitivity: 'base' });
        } else if (sortField === 'appDisplayName') {
            cmp = (a.appDisplayName || '').localeCompare(b.appDisplayName || '', undefined, { sensitivity: 'base' });
        } else if (sortField === 'clientId') {
            cmp = (a.clientId || '').localeCompare(b.clientId || '', undefined, { sensitivity: 'base' });
        }
        return sortAsc ? cmp : -cmp;
    });

    const handleSort = (field) => {
        if (sortField === field) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            setSortAsc(true);
        }
    };

    const getSortIndicator = (field) => {
        if (sortField !== field) return '';
        return sortAsc ? ' ▲' : ' ▼';
    };

    // Summary stats
    const expiredCount = inventory.filter(e => getWorstStatus(e.credentials) === 'Expired').length;
    const expiringCount = inventory.filter(e => getWorstStatus(e.credentials) === 'ExpiringSoon').length;
    const validCount = inventory.filter(e => getWorstStatus(e.credentials) === 'Valid').length;
    const staleRefCount = inventory.filter(hasStaleOnlyRef).length;
    const outdatedRefCount = inventory.filter(hasOutdatedRef).length;
    const duplicateRefCount = inventory.filter(hasDuplicateRef).length;
    const unknownCount = inventory.filter(e => getWorstStatus(e.credentials) === 'Unknown').length;
    const totalRefsCount = inventory.reduce((sum, e) => sum + (Array.isArray(e.keyVaultRefs) ? e.keyVaultRefs.length : 0), 0);
    const validRefsCount = inventory.reduce((sum, e) => sum + (Array.isArray(e.keyVaultRefs) ? e.keyVaultRefs.filter(r => r && r.isCurrent === true).length : 0), 0);

    if (!hasAccess) {
        return (
            <div className="kvm-container">
                <h2 className="kvm-title">KeyVault Manager</h2>
                <p>You do not have access to this page.</p>
            </div>
        );
    }

    return (
        <div className="kvm-container">
            <h2 className="kvm-title">KeyVault Manager</h2>

            {/* Summary bar */}
            <div className="kvm-summary">
                <div className="kvm-summary-group">
                    <span className="kvm-summary-group-label">Credentials</span>
                    <div className="kvm-summary-group-items">
                        <div className="kvm-summary-item">
                            <span className="kvm-summary-count">{inventory.length}</span>
                            <span className="kvm-summary-label">Total</span>
                        </div>
                        <div className="kvm-summary-item kvm-summary-valid">
                            <span className="kvm-summary-count">{validCount}</span>
                            <span className="kvm-summary-label">Valid</span>
                        </div>
                        <div className="kvm-summary-item kvm-summary-expired">
                            <span className="kvm-summary-count">{expiredCount}</span>
                            <span className="kvm-summary-label">Expired</span>
                        </div>
                        <div className="kvm-summary-item kvm-summary-expiring">
                            <span className="kvm-summary-count">{expiringCount}</span>
                            <span className="kvm-summary-label">Expiring Soon</span>
                        </div>
                        <div className="kvm-summary-item kvm-summary-unknown">
                            <span className="kvm-summary-count">{unknownCount}</span>
                            <span className="kvm-summary-label">Unknown</span>
                        </div>
                    </div>
                </div>
                <div className="kvm-summary-divider"></div>
                <div className="kvm-summary-group">
                    <span className="kvm-summary-group-label">Key Vault Refs</span>
                    <div className="kvm-summary-group-items">
                        <div className="kvm-summary-item">
                            <span className="kvm-summary-count">{totalRefsCount}</span>
                            <span className="kvm-summary-label">Total</span>
                        </div>
                        <div className="kvm-summary-item kvm-summary-valid">
                            <span className="kvm-summary-count">{validRefsCount}</span>
                            <span className="kvm-summary-label">Valid</span>
                        </div>
                        <div className="kvm-summary-item kvm-summary-outdated">
                            <span className="kvm-summary-count">{outdatedRefCount}</span>
                            <span className="kvm-summary-label">Outdated</span>
                        </div>
                        <div className="kvm-summary-item kvm-summary-stale">
                            <span className="kvm-summary-count">{staleRefCount}</span>
                            <span className="kvm-summary-label">Stale</span>
                        </div>
                        <div className="kvm-summary-item kvm-summary-duplicate">
                            <span className="kvm-summary-count">{duplicateRefCount}</span>
                            <span className="kvm-summary-label">Duplicate</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and refresh */}
            <div className="kvm-toolbar">
                <input
                    type="text"
                    className="kvm-search"
                    placeholder="Search by name, vault, client ID, app name, or scope..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="kvm-refresh-button" onClick={fetchInventory} disabled={isLoading}>
                    {isLoading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            {isLoading && <LoadingSpinner message="Scanning Key Vaults and App Registrations..." size="large" />}

            {!isLoading && sorted.length === 0 && (
                <p className="kvm-empty">No credentials found.</p>
            )}

            {!isLoading && sorted.length > 0 && (
                <table className="kvm-table">
                    <thead>
                        <tr>
                            <th className="kvm-th-expand"></th>
                            <th className="kvm-th-sortable" onClick={() => handleSort('name')}>Name{getSortIndicator('name')}</th>
                            <th className="kvm-th-sortable" onClick={() => handleSort('keyVaultName')}>Key Vault{getSortIndicator('keyVaultName')}</th>
                            <th className="kvm-th-sortable" onClick={() => handleSort('clientId')}>Client ID{getSortIndicator('clientId')}</th>
                            <th className="kvm-th-sortable" onClick={() => handleSort('appDisplayName')}>App Name{getSortIndicator('appDisplayName')}</th>
                            <th className="kvm-th-sortable" onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((entry, index) => {
                            const worstStatus = getWorstStatus(entry.credentials);
                            const isExpanded = expandedRows[index];
                            return (
                                <React.Fragment key={index}>
                                    <tr className={`kvm-row kvm-row-${worstStatus.toLowerCase()}`} onClick={() => toggleRow(index)}>
                                        <td className="kvm-cell-expand">{(entry.credentials && entry.credentials.length > 0) || getWorstStatus(entry.credentials) === 'Unknown' ? (isExpanded ? '▼' : '▶') : ''}</td>
                                        <td>{entry.name}</td>
                                        <td>{entry.keyVaultNames && entry.keyVaultNames.length > 1
                            ? <span title={entry.keyVaultNames.join(', ')}>{entry.keyVaultNames[0]} <em>(+{entry.keyVaultNames.length - 1} more)</em></span>
                            : entry.keyVaultName}</td>
                                        <td className="kvm-cell-clientid" title={entry.clientId}>
                                            <span>{truncateId(entry.clientId)}</span>
                                            <button className="kvm-copy-button" onClick={(e) => { e.stopPropagation(); copyToClipboard(entry.clientId); }} title="Copy Client ID">📋</button>
                                        </td>
                                        <td>{entry.appDisplayName || '—'}</td>
                                        <td>
                                            {getStatusBadge(worstStatus)}
                                            {hasOutdatedRef(entry) && (
                                                <span
                                                    className="kvm-outdated-indicator"
                                                    title="At least one Key Vault reference has an outdated secret with no valid replacement in the same vault."
                                                >
                                                    ⚠ Outdated
                                                </span>
                                            )}
                                            {hasStaleOnlyRef(entry) && (
                                                <span
                                                    className="kvm-stale-indicator"
                                                    title="At least one Key Vault reference has a stale secret, but a valid replacement exists in the same vault."
                                                >
                                                    ⚠ Stale Ref
                                                </span>
                                            )}
                                            {hasDuplicateRef(entry) && (
                                                <span
                                                    className="kvm-duplicate-indicator"
                                                    title="The same Key Vault appears more than once in the references for this credential."
                                                >
                                                    ⚠ Duplicate Ref
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className="kvm-detail-row">
                                            <td colSpan="6">
                                                {entry.keyVaultRefs && entry.keyVaultRefs.length > 0 ? (
                                                    <div className="kvm-vault-list">
                                                        <strong>Key Vault references (hint match vs App Registration):</strong>
                                                        <div className="kvm-vault-tags">
                                                            {(() => {
                                                                const dupNames = getDuplicateVaultNames(entry);
                                                                const hasStale = hasStaleRef(entry);
                                                                const seen = new Set();
                                                                return entry.keyVaultRefs
                                                                .filter(ref => {
                                                                    // When no stale refs, deduplicate by vault name
                                                                    if (!hasStale && dupNames.size > 0) {
                                                                        const key = (ref.keyVaultName || '').toLowerCase();
                                                                        if (seen.has(key)) return false;
                                                                        seen.add(key);
                                                                    }
                                                                    return true;
                                                                })
                                                                .map((ref, vi) => {
                                                                const isDup = dupNames.has((ref.keyVaultName || '').toLowerCase());
                                                                const resourceId = entry.keyVaultResourceIds && entry.keyVaultResourceIds[ref.keyVaultName];
                                                                const portalUrl = resourceId
                                                                    ? `https://portal.azure.com/#@Odfjell.onmicrosoft.com/resource${resourceId}/overview`
                                                                    : null;
                                                                let badgeClass = 'kvm-ref-unknown';
                                                                let badgeLabel = 'Unknown';
                                                                let badgeTitle = 'Cannot determine — App Registration not found, no active password credentials, or KV secret too short to compute hint.';
                                                                if (ref.isCurrent === true) {
                                                                    badgeClass = 'kvm-ref-current';
                                                                    badgeLabel = '✓ Current';
                                                                    badgeTitle = ref.matchedAppCredentialName
                                                                        ? `Matches active App Registration secret "${ref.matchedAppCredentialName}".`
                                                                        : 'Matches an active App Registration secret hint.';
                                                                } else if (ref.isCurrent === false) {
                                                                    badgeClass = 'kvm-ref-stale';
                                                                    badgeLabel = '⚠ Outdated';
                                                                    badgeTitle = "First 3 chars don't match any active App Registration secret hint — likely a rotated or expired credential still stored here.";
                                                                }
                                                                const hint = ref.clientSecretHint ? ` · ${ref.clientSecretHint}…` : '';
                                                                const tag = (
                                                                    <>
                                                                        <span className="kvm-ref-vault">{ref.keyVaultName}</span>
                                                                        <span className="kvm-ref-hint">{hint}</span>
                                                                        <span className={`kvm-ref-badge ${badgeClass}`} title={badgeTitle}>{badgeLabel}</span>
                                                                        {isDup && <span className="kvm-ref-badge kvm-ref-duplicate" title="This vault appears more than once in the references — duplicate Key Vault entry">⚠</span>}
                                                                    </>
                                                                );
                                                                return portalUrl ? (
                                                                    <a
                                                                        key={vi}
                                                                        className="kvm-vault-tag"
                                                                        href={portalUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title={`Open ${ref.keyVaultName} in Azure Portal${isDup ? ' (duplicate reference)' : ''}`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {tag}
                                                                    </a>
                                                                ) : (
                                                                    <span key={vi} className="kvm-vault-tag">{tag}</span>
                                                                );
                                                            });
                                                            })()}
                                                        </div>
                                                    </div>
                                                ) : entry.keyVaultNames && entry.keyVaultNames.length > 0 && (
                                                    <div className="kvm-vault-list">
                                                        <strong>Present in Key Vaults:</strong>
                                                        <div className="kvm-vault-tags">
                                                            {entry.keyVaultNames.map((vaultName, vi) => {
                                                                const resourceId = entry.keyVaultResourceIds && entry.keyVaultResourceIds[vaultName];
                                                                const portalUrl = resourceId
                                                                    ? `https://portal.azure.com/#@Odfjell.onmicrosoft.com/resource${resourceId}/overview`
                                                                    : null;
                                                                return portalUrl ? (
                                                                    <a
                                                                        key={vi}
                                                                        className="kvm-vault-tag"
                                                                        href={portalUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title={`Open ${vaultName} in Azure Portal`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {vaultName}
                                                                    </a>
                                                                ) : (
                                                                    <span key={vi} className="kvm-vault-tag">{vaultName}</span>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                                {(!entry.credentials || entry.credentials.length === 0) ? (
                                                    <div className="kvm-unknown-detail">
                                                        <strong>⚠ Status Unknown — </strong>
                                                        {!entry.appDisplayName
                                                            ? 'No matching App Registration found in Entra for this Client ID. This is typically a third-party credential or a deprecated application that has been removed.'
                                                            : 'The App Registration was found but has no password or certificate credentials. The credentials may have been removed or the application is deprecated.'}
                                                        {entry.keyVaultRefs && entry.keyVaultRefs.some(r => r && r.isCurrent === null) && (
                                                            <span> Hint match could not be determined because the App Registration might not have values, or lookup failed.</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                <table className="kvm-detail-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Type</th>
                                                            <th>Name</th>
                                                            <th>Hint</th>
                                                            <th>Start Date</th>
                                                            <th>End Date</th>
                                                            <th>Days Left</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {entry.credentials.map((cred, ci) => (
                                                            <tr key={ci} className={`kvm-detail-${(cred.status || 'unknown').toLowerCase()}`}>
                                                                <td>{cred.type}</td>
                                                                <td>{cred.displayName || '—'}</td>
                                                                <td>{cred.hint || '—'}</td>
                                                                <td>{cred.startDateTime ? new Date(cred.startDateTime).toLocaleDateString() : '—'}</td>
                                                                <td>{cred.endDateTime ? new Date(cred.endDateTime).toLocaleDateString() : '—'}</td>
                                                                <td>{cred.daysUntilExpiry != null ? cred.daysUntilExpiry : '—'}</td>
                                                                <td>{getStatusBadge(cred.status || 'Unknown')}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

KeyVaultManager.propTypes = {
    config: PropTypes.shape({
        logMsgApiUrl: PropTypes.string.isRequired,
        clientId: PropTypes.string.isRequired,
        enviromentScope: PropTypes.string.isRequired,
        tokenCredentialGroupIds: PropTypes.string,
    }).isRequired,
};

export default KeyVaultManager;
